import { DrawFocus, SafeHtml } from '@thg-commerce/gravity-elements'
import { css, styled } from '@thg-commerce/gravity-patterns/theme'
import { keyframes, Text } from '@thg-commerce/gravity-theme'

const StyledText = css`
  ${(props) =>
    Text(
      props.theme.widgets.stripBanner.font.entry,
      props.theme.widgets.stripBanner.font.style,
    )};
  color: ${(props) => props.theme.widgets.stripBanner.font.textColor};
  text-transform: ${(props) => props.theme.widgets.stripBanner.font.transform};
  text-decoration: ${(props) =>
    props.theme.widgets.stripBanner.font.textDecoration};
`

const visibility = (panelCount: number) => keyframes`
  0% { visibility: visible }
  ${100 / panelCount}% { visibility: hidden }
`

export const StripBannerSlides = styled.div<{
  panelCount: number
  index: number
}>`
  width: 100%;
  justify-content: center;
  visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  animation: ${(props) => props.panelCount * 2}s
    ${(props) => visibility(props.panelCount)} infinite;
  animation-delay: ${(props) => props.index * 2}s;

  &:first-child {
    position: static;
  }
`

export const StyledHref = styled.a`
  ${StyledText}
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  padding-right: 2px;
  ${DrawFocus()};
  text-align: center;
  color: ${(props) => props.theme.widgets.stripBanner.font.textColor};
  border: 3px solid transparent;
  touch-action: manipulation;

  &::after {
    ${StyledText}
    display: block;
    content: 'P';
    height: 0;
    visibility: hidden;
  }

  &:hover {
    background-color: ${(props) =>
      props.theme.widgets.stripBanner.hoverBackgroundColor};
    color: ${(props) => props.theme.widgets.stripBanner.hoverTextColor};
    text-decoration: ${(props) =>
      props.theme.widgets.stripBanner.hoverTextDecoration};
  }

  &:focus-visible {
    text-decoration: underline;
    border: ${(props) =>
      `${props.theme.widgets.stripBanner.border.focusWidth}
      solid
      ${
        props.theme.widgets.stripBanner.border.focusColor ||
        props.theme.colors.palette.brand.base
      }`};
    background-color: ${(props) =>
      props.theme.widgets.stripBanner.focusBackgroundColor};
  }
`

export const StyledSVGWrapper = styled.div<{
  useStrokeFill?: boolean
  isClipBoard?: boolean
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
`

export const NoLinkContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  ${StyledText};
  text-align: center;

  &::after {
    ${StyledText}
    display: block;
    content: 'P';
    height: 0;
    visibility: hidden;
  }
`
export const StripBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  align-items: center;
  flex-direction: column;
  background-color: ${(props) =>
    props.theme.widgets.stripBanner.backgroundColor};
  touch-action: manipulation;
`

export const StyledSafeHtml = styled(SafeHtml)`
  ${StyledText}

  * {
    ${StyledText}
  }
`
export const FlyoutMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  opacity: 1;
  visibility: hidden;
  transition: opacity 0.3s ease-in-out, visibility 0.5s ease-in-out;
  touch-action: manipulation;

  &.open {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.3s ease-in-out, visibility 0.5s ease-in-out;
  }
`

export const FlyoutMenuContainer = styled.div`
  position: relative;
  background-color: white;
  width: 85%;
  max-width: 320px;
  height: 100%;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  z-index: 1001;
  transform: translateX(100%);
  transition: transform 0.6s ease-in-out;
  touch-action: manipulation;

  &.open {
    opacity: 1;
    transform: translateX(0);
    visibility: visible;
    transition: transform 0.6s ease-in-out;
  }

  &.closing {
    transform: translateX(100%);
  }
`

export const FlyoutMenuHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  touch-action: manipulation;
`

export const FlyoutMenuContent = styled.div`
  padding: 0;
`

export const PromoSection = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #bcbcbc;
  margin: 0 16px;
`

export const PromoTitle = styled.h1`
  font-size: ${(props) =>
    props.theme.widgets.OffersPanel?.title.fontSize || '16px'};
  font-weight: ${(props) =>
    props.theme.widgets.OffersPanel?.title.fontWeight || '600'};
  color: ${(props) =>
    props.theme.widgets.OffersPanel?.title.fontColor || '#050505'};
  margin: 0 0 4px 0;
  line-height: 24px;
`

export const PromoDescription = styled.p`
  margin: 16px 0 16px 0;
  font-size: ${(props) =>
    props.theme.widgets.OffersPanel?.description.fontSize || '15px'};
  line-height: 1.4;
  color: ${(props) =>
    props.theme.widgets.OffersPanel?.description.color || '#050505'};
`

export const PromoButton = styled.button<{}>`
  background-color: ${(props) =>
    props.theme.widgets.OffersPanel?.backgroundColor || '#AD1E1F'};
  border: none;
  font-family: ${(props) =>
    props.theme.widgets.OffersPanel?.description.fontFamily || 'FuturaPT'};
  padding: 8px 16px;
  font-size: ${(props) =>
    props.theme.widgets.OffersPanel?.title.fontSize || '16px'};
  font-weight: ${(props) =>
    props.theme.widgets.OffersPanel?.title.fontWeight || '600'};
  cursor: pointer;
  width: 186px;
  color: ${(props) =>
    props.theme.widgets.OffersPanel?.title.color || '#ffffff'};
  touch-action: manipulation;
`
